/**
 * SkateparkHours - Client-side day detection for opening hours display
 *
 * Automatically detects the current day of the week and updates block displays
 * with today's opening hours from data attributes.
 *
 * Usage:
 *   <div data-skatepark-hours
 *        data-hours-monday="12pm - 8pm"
 *        data-hours-tuesday="12pm - 8pm"
 *        ...>
 *     <span data-hours-display></span>
 *   </div>
 *
 * @class SkateparkHours
 */
// Prevent duplicate declaration if script is loaded multiple times
if (typeof SkateparkHours === 'undefined') {
class SkateparkHours {
  /**
   * Initialize hours display for a block element
   * @param {HTMLElement} element - Block element with data-skatepark-hours attribute
   */
  constructor(element) {
    if (!element) {
      console.error('SkateparkHours: element is required');
      return;
    }

    this.element = element;
    this.hoursDisplay = element.querySelector('[data-hours-display]');
    this.coachingDisplay = element.querySelector('[data-coaching-display]');

    if (!this.hoursDisplay) {
      console.warn('SkateparkHours: [data-hours-display] element not found');
      return;
    }

    // Cache alert container reference
    this.alertContainer = element.querySelector('[data-alert-container]');

    // Initialize immediately
    this.updateHours();
    this.updateAlerts();
  }

  /**
   * Detect current day and update display with today's hours
   * Maps JavaScript Date.getDay() (0=Sunday, 6=Saturday) to data attributes
   */
  updateHours() {
    // Day name mapping (0=Sunday per JavaScript Date.getDay() spec)
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    // Get current day index (0-6)
    const today = new Date().getDay();
    const dayName = days[today] || 'monday'; // Fallback to monday if invalid

    // Convert to dataset property name (e.g., 'monday' -> 'hoursMonday')
    const datasetKey = `hours${dayName.charAt(0).toUpperCase()}${dayName.slice(1)}`;

    // Read hours from data attribute with fallback to default
    const hours = this.element.dataset[datasetKey] || '12pm - 8pm';

    // Update display (use textContent for XSS safety, not innerHTML)
    if (this.hoursDisplay) {
      this.hoursDisplay.textContent = hours;
    }

    // Show today's coaching time, hide on days without coaching
    if (this.coachingDisplay) {
      const coachingKey = `coaching${dayName.charAt(0).toUpperCase()}${dayName.slice(1)}`;
      const coaching = this.element.dataset[coachingKey] || '';
      this.coachingDisplay.textContent = coaching ? `Coaching ${coaching}` : '';
      this.coachingDisplay.hidden = !coaching;
    }
  }

  /**
   * Parse and display active alert based on current date
   * Applies priority: closure > event > modified_hours
   */
  updateAlerts() {
    if (!this.alertContainer) {
      return; // Block doesn't have alert support (backward compat)
    }

    const alertsData = this.element.dataset.alerts;
    const timezoneOffset = this.element.dataset.timezoneOffset;

    if (!alertsData || alertsData === '') {
      this.alertContainer.style.display = 'none';
      return;
    }

    const activeAlert = this.getActiveAlert(alertsData, timezoneOffset);

    if (activeAlert) {
      this.renderAlert(activeAlert);
      // During a closure, show "Closed" instead of today's hours —
      // opening times next to a "closed" banner are contradictory.
      if (activeAlert.type === 'closure') {
        this.setClosed();
      }
    } else {
      this.alertContainer.style.display = 'none';
    }
  }

  /**
   * Override the "Open Today" value with "Closed" (used during closure alerts)
   */
  setClosed() {
    if (this.hoursDisplay) {
      this.hoursDisplay.textContent = 'Closed';
    }
    // No coaching when the park is closed
    if (this.coachingDisplay) {
      this.coachingDisplay.hidden = true;
    }
  }

  /**
   * Calculate next occurrence of a recurring event from a start date
   * @param {string} eventDateStr - YYYY-MM-DD start date
   * @param {string} recurrence - 'weekly', 'fortnightly', or 'monthly'
   * @param {string} recurrenceEndStr - YYYY-MM-DD end date for recurrence
   * @returns {Date|null} - Next occurrence date or null if series ended
   */
  getNextOccurrence(eventDateStr, recurrence, recurrenceEndStr) {
    const eventDate = this.parseDate(eventDateStr);
    if (!eventDate) return null;

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const recurrenceEnd = recurrenceEndStr ? this.parseDate(recurrenceEndStr) : null;

    let candidate = new Date(eventDate);

    // Advance candidate to first occurrence >= today
    while (candidate < now) {
      if (recurrence === 'weekly') {
        candidate.setDate(candidate.getDate() + 7);
      } else if (recurrence === 'fortnightly') {
        candidate.setDate(candidate.getDate() + 14);
      } else if (recurrence === 'monthly') {
        const origDay = eventDate.getDate();
        candidate.setMonth(candidate.getMonth() + 1);
        // Handle month overflow (e.g., Jan 31 -> Feb 28)
        if (candidate.getDate() !== origDay) {
          candidate.setDate(0); // Last day of previous month
        }
      } else {
        return null;
      }
    }

    // Check if past recurrence end date
    if (recurrenceEnd && candidate > recurrenceEnd) {
      return null;
    }

    return candidate;
  }

  /**
   * Parse alerts data and return highest-priority active alert
   * Supports both legacy 4-field and new 7-field format with recurrence
   * @param {string} alertsData - Pipe-separated alert strings
   * @param {string} timezoneOffset - Shopify timezone offset (e.g., '+0000')
   * @returns {Object|null} - Active alert object or null
   */
  getActiveAlert(alertsData, timezoneOffset) {
    const alertStrings = alertsData.split('|');
    const now = new Date();
    const activeAlerts = [];

    for (const alertString of alertStrings) {
      const parts = alertString.split('::');
      const type = parts[0];
      const message = parts[1];

      if (!type || !message) {
        continue;
      }

      // New 7-field format: type::message::eventDate::eventEndDate::recurrence::recurrenceEndDate::alertDaysBefore
      // Legacy 4-field format: type::message::startDate::endDate
      const eventDate = parts[2] || '';
      const eventEndDate = parts[3] || '';
      const recurrence = parts[4] || 'none';
      const recurrenceEndDate = parts[5] || '';
      const alertDaysBefore = parseInt(parts[6] || '0', 10) || 0;

      // Determine if this is new format (has recurrence field) or legacy
      const isNewFormat = parts.length >= 5;

      if (isNewFormat && recurrence !== 'none') {
        // Recurring event: find next occurrence and check alert window
        const nextOcc = this.getNextOccurrence(eventDate, recurrence, recurrenceEndDate);
        if (!nextOcc) continue; // Series ended

        const alertStart = new Date(nextOcc);
        alertStart.setDate(alertStart.getDate() - alertDaysBefore);
        alertStart.setHours(0, 0, 0, 0);

        const alertEnd = new Date(nextOcc);
        alertEnd.setHours(23, 59, 59, 999);

        if (now >= alertStart && now <= alertEnd) {
          activeAlerts.push({ type, message });
        }
      } else if (isNewFormat) {
        // Non-recurring event with new format: use eventDate - alertDaysBefore as start, eventEndDate as end
        if (!eventDate) continue;

        const evDate = this.parseDate(eventDate, timezoneOffset);
        if (!evDate) continue;

        const alertStart = new Date(evDate);
        alertStart.setDate(alertStart.getDate() - alertDaysBefore);
        alertStart.setHours(0, 0, 0, 0);

        const endStr = eventEndDate || eventDate;
        const evEnd = this.parseDate(endStr, timezoneOffset);
        if (!evEnd) continue;

        const alertEnd = new Date(evEnd);
        alertEnd.setHours(23, 59, 59, 999);

        if (now >= alertStart && now <= alertEnd) {
          activeAlerts.push({ type, message });
        }
      } else {
        // Legacy 4-field format: type::message::startDate::endDate
        const startDate = parts[2] || '';
        const endDate = parts[3] || '';
        const hasStart = startDate && startDate.trim() !== '';
        const hasEnd = endDate && endDate.trim() !== '';

        if (!hasStart && !hasEnd) {
          activeAlerts.push({ type, message });
        } else if (hasStart && !hasEnd) {
          const start = this.parseDate(startDate, timezoneOffset);
          if (start && now >= start) {
            activeAlerts.push({ type, message });
          }
        } else if (hasStart && hasEnd) {
          const start = this.parseDate(startDate, timezoneOffset);
          const end = this.parseDate(endDate, timezoneOffset);
          if (start && end) {
            const endOfDay = new Date(end);
            endOfDay.setHours(23, 59, 59, 999);
            if (now >= start && now <= endOfDay) {
              activeAlerts.push({ type, message });
            }
          }
        }
      }
    }

    if (activeAlerts.length === 0) {
      return null;
    }

    // Apply priority: closure > event > modified_hours
    const priorityOrder = { closure: 1, event: 2, modified_hours: 3 };
    activeAlerts.sort((a, b) => {
      return (priorityOrder[a.type] || 99) - (priorityOrder[b.type] || 99);
    });

    return activeAlerts[0];
  }

  /**
   * Parse YYYY-MM-DD string to Date object with timezone awareness
   * @param {string} dateString - YYYY-MM-DD format
   * @param {string} timezoneOffset - Shopify timezone offset (e.g., '+0000')
   * @returns {Date|null} - Parsed date or null if invalid
   */
  parseDate(dateString, timezoneOffset) {
    // Validate format (basic regex)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      console.warn('SkateparkHours: Invalid date format', dateString);
      return null;
    }

    // Parse as local midnight (not UTC midnight)
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day, 0, 0, 0, 0);

    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  }

  /**
   * Render alert banner in DOM
   * @param {Object} alert - Alert object with type and message
   */
  renderAlert(alert) {
    // Map alert type to CSS modifier class
    const typeClass = `skatepark-info__alert--${alert.type}`;

    // Determine icon based on type
    const icons = {
      closure: '⚠️',
      event: '📅',
      modified_hours: '🕐'
    };
    const icon = icons[alert.type] || '📢';

    // Build HTML (using textContent for XSS safety)
    const iconSpan = document.createElement('span');
    iconSpan.className = 'skatepark-info__alert-icon';
    iconSpan.setAttribute('aria-hidden', 'true');
    iconSpan.textContent = icon;

    const messageSpan = document.createElement('span');
    messageSpan.className = 'skatepark-info__alert-message';
    messageSpan.textContent = alert.message; // XSS-safe

    // Clear previous content and append new
    this.alertContainer.innerHTML = '';
    this.alertContainer.className = `skatepark-info__alert ${typeClass}`;
    this.alertContainer.appendChild(iconSpan);
    this.alertContainer.appendChild(messageSpan);
    this.alertContainer.style.display = 'flex';
  }
}

/**
 * Auto-initialization on DOM ready
 * Initializes all elements with [data-skatepark-hours] attribute
 */
document.addEventListener('DOMContentLoaded', () => {
  const blocks = document.querySelectorAll('[data-skatepark-hours]');

  blocks.forEach(block => {
    new SkateparkHours(block);
  });
});
} // End of duplicate declaration guard
